import ThemeMarqueeEngine from '@theme/lib-marquee';
import {
  debounce,
  ensureMarqueeSetCopies,
  getMarqueeDirectionMultiplier,
  isMobile,
  shouldReduceMotion,
} from '@theme/utilities';

class MarqueeController {
  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.element = element;
    /** @type {HTMLElement | null} */
    this.viewport = null;
    /** @type {HTMLElement | null} */
    this.track = null;
    /** @type {HTMLElement | null} */
    this.templateSet = null;

    this.speed = 60;
    this.direction = 'left';
    this.pauseOnHover = true;
    this.pauseOnFocus = true;

    /** @type {ThemeMarqueeEngine | null} */
    this.engine = null;
    /** @type {ReturnType<typeof debounce<() => void>> | null} */
    this.resizeHandler = null;
    /** @type {ResizeObserver | null} */
    this.resizeObserver = null;
    this.viewportWidth = 0;
    this.prefersReducedMotion = false;
    this.isDocumentHidden = false;

    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    this.initialize();
  }

  initialize() {
    this.viewport = this.element.querySelector('[data-marquee-viewport]');
    this.track = this.element.querySelector('[data-marquee-track]');

    if (!this.viewport || !this.track) {
      return;
    }

    this.parseConfig();

    const sourceSet = this.element.querySelector('[data-marquee-template]');
    if (!(sourceSet instanceof HTMLElement)) {
      return;
    }

    this.templateSet = this.createTemplateSet(sourceSet);

    this.setupEngine();
    this.attachEvents();
    this.refresh({ force: true });
    this.element.setAttribute('data-marquee-ready', '');

    if (this.engine && this.speed > 0) {
      this.engine.start();
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.element.isConnected) {
          this.refresh({ force: true });
        }
      });
    }
  }

  destroy() {
    this.detachEvents();

    if (this.engine) {
      this.engine.destroy();
      this.engine = null;
    }
  }

  parseConfig() {
    this.speed = Number(this.element.dataset.speed) || 60;
    this.direction = this.element.dataset.direction || 'left';
    this.pauseOnHover = this.element.dataset.pauseOnHover !== 'false';
    this.pauseOnFocus = this.element.dataset.pauseOnFocus !== 'false';
    this.prefersReducedMotion = shouldReduceMotion();

    if (this.prefersReducedMotion) {
      this.speed = 0;
    }
  }

  /**
   * @param {HTMLElement} sourceSet
   * @returns {HTMLElement}
   */
  createTemplateSet(sourceSet) {
    const templateSet = sourceSet.cloneNode(true);

    if (!(templateSet instanceof HTMLElement)) {
      return sourceSet;
    }

    templateSet.removeAttribute('data-marquee-template');
    templateSet.removeAttribute('aria-hidden');
    templateSet.removeAttribute('data-marquee-clone');

    return templateSet;
  }

  setupEngine() {
    if (this.engine) {
      this.engine.destroy();
    }

    this.engine = new ThemeMarqueeEngine({
      track: this.track,
      viewport: this.viewport,
      speed: this.speed,
      directionMultiplier: getMarqueeDirectionMultiplier(this.direction),
      getLoopWidth: () => {
        const set = this.track?.querySelector('[data-marquee-set]');
        return set ? set.getBoundingClientRect().width : 0;
      },
    });
  }

  attachEvents() {
    if (this.pauseOnHover) {
      this.element.addEventListener('mouseenter', this.handleMouseEnter);
      this.element.addEventListener('mouseleave', this.handleMouseLeave);
    }

    if (this.pauseOnFocus) {
      this.element.addEventListener('focusin', this.handleFocusIn);
      this.element.addEventListener('focusout', this.handleFocusOut);
    }

    this.resizeHandler = debounce(() => this.refresh(), 200);
    window.addEventListener('resize', this.resizeHandler);

    if (window.ResizeObserver && this.viewport) {
      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this.viewport);
    }

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.isDocumentHidden = document.hidden;
    this.syncPauseState();
  }

  detachEvents() {
    this.element.removeEventListener('mouseenter', this.handleMouseEnter);
    this.element.removeEventListener('mouseleave', this.handleMouseLeave);
    this.element.removeEventListener('focusin', this.handleFocusIn);
    this.element.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  handleMouseEnter() {
    if (this.engine) {
      this.engine.pause();
    }
  }

  handleMouseLeave() {
    this.syncPauseState();
  }

  handleFocusIn() {
    if (this.engine) {
      this.engine.pause();
    }
  }

  handleFocusOut() {
    this.syncPauseState();
  }

  handleVisibilityChange() {
    this.isDocumentHidden = document.hidden;
    this.syncPauseState();
  }

  syncPauseState() {
    if (!this.engine) {
      return;
    }

    if (this.isDocumentHidden) {
      this.engine.pause();
      return;
    }

    this.engine.resume();
  }

  /**
   * @param {{ force?: boolean }} [options]
   */
  refresh(options = {}) {
    if (!this.track || !this.viewport || !this.templateSet || !this.engine) {
      return;
    }

    const force = options.force === true;
    const viewportWidth = this.viewport.getBoundingClientRect().width;

    if (!viewportWidth) {
      return;
    }

    if (!force && Math.abs(viewportWidth - this.viewportWidth) < 1) {
      return;
    }

    this.viewportWidth = viewportWidth;
    const clonedSet = this.templateSet.cloneNode(true);
    if (!(clonedSet instanceof HTMLElement)) {
      return;
    }

    const { width: setWidth } = ensureMarqueeSetCopies(
      this.track,
      clonedSet,
      viewportWidth * 2
    );

    this.engine.setLoopWidth(setWidth, { preserveOffset: !force });
  }
}

/** @type {Map<HTMLElement, MarqueeController>} */
const marqueeInstances = new Map();

/**
 * @param {ParentNode} [scope]
 * @returns {HTMLElement[]}
 */
function getMarqueeElements(scope = document) {
  return Array.from(scope.querySelectorAll('[data-marquee]'));
}

/**
 * @param {ParentNode} [scope]
 */
function initMarquees(scope = document) {
  getMarqueeElements(scope).forEach((element) => {
    if (marqueeInstances.has(element)) {
      return;
    }

    marqueeInstances.set(element, new MarqueeController(element));
  });
}

/**
 * @param {() => void} callback
 */
function scheduleMarqueeInit(callback) {
  const isMobileViewport = isMobile();
  if (!isMobileViewport) {
    callback();
    return;
  }

  const run = function () {
    callback();
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(run, { timeout: 2000 });
    return;
  }

  if (document.readyState === 'complete') {
    setTimeout(run, 0);
    return;
  }

  globalThis.addEventListener(
    'load',
    function () {
      setTimeout(run, 0);
    },
    { once: true }
  );
}

/**
 * @param {ParentNode} [scope]
 */
function scheduleInitMarquees(scope = document) {
  scheduleMarqueeInit(function () {
    initMarquees(scope);
  });
}

/**
 * @param {ParentNode} [scope]
 */
function destroyMarquees(scope = document) {
  getMarqueeElements(scope).forEach((element) => {
    const instance = marqueeInstances.get(element);

    if (!instance) {
      return;
    }

    instance.destroy();
    marqueeInstances.delete(element);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    scheduleInitMarquees();
  });
} else {
  scheduleInitMarquees();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    scheduleInitMarquees(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target instanceof HTMLElement) {
    destroyMarquees(event.target);
  }
});
